import { useShortIdContext } from '@air/provider-short-id';

import { usePublicShowcaseActiveSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseActiveSubPage';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

/**
 * Whether the collection link is currently showing an IMMERSIVE cover — the full-bleed cover that
 * bleeds under the workspace header (the page pulls its scroll area up behind it and the header
 * paints its surface only as the cover scrolls away).
 *
 * The ONE reading of that state, shared by the page (which composes the immersive layout) and the
 * workspace header (which fades its surface in over the cover). Both used to compute it separately
 * and disagreed: the header's copy read only the link-level cover, so it went transparent on a
 * sub-page with no cover and while drilled into a board — leaving its nav elements floating over a
 * page that was rendering no cover at all.
 *
 * The four halves of the predicate:
 *  - a cover IMAGE must actually exist to bleed under the header (`coverWidth: 'immersive'` with no
 *    image renders the standard chrome);
 *  - the image is resolved PER PAGE — the active sub-page shows its OWN cover, the root page the
 *    link-wide one (mirrors PublicShowcaseCover's resolution), while `coverWidth` stays root-only;
 *  - off while drilled into a board: the drilled board renders in the standard column region with
 *    its own cover, so the immersive treatment is a collection-page-only concern;
 *  - off while searching: the search results page renders no cover at all.
 *
 * Reads `presentationSettings` from context rather than props so both consumers resolve the same
 * value with no threading; every surface that mounts this tree (the live page, the editor's Preview
 * iframe, the editor's inline canvas) feeds its ShortIdProvider and the page the same settings
 * object (see PublicShowcaseLayout).
 */
export const usePublicShowcaseImmersiveCover = () => {
  const { presentationSettings } = useShortIdContext();
  const { activeBoardId: drilledBoardId } = usePublicShowcaseContext();
  const { isSearchActive } = useIsSearchActive();
  const activeSubPage = usePublicShowcaseActiveSubPage();

  const activePageCoverImage = activeSubPage ? activeSubPage.settings?.coverImage : presentationSettings?.coverImage;

  return {
    hasImmersiveCover:
      !drilledBoardId &&
      !isSearchActive &&
      !!activePageCoverImage?.url &&
      presentationSettings?.coverWidth === 'immersive',
  };
};
